import {Directive, ElementRef, Inject, Input} from '@angular/core';
import {Subject} from 'rxjs';
import {FormFieldInstance} from '../model/FormFieldInstance';
import {BoAutocompleteOption} from '../model/BoAutocompleteOption';
import {DYNAMIC_FORMS_SERVICE, DynamicFormsService} from '../lib/DynamicFormsService';
import {debounceTime, distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {DEBOUNCE_TIME} from '../lib/Util';
import {DynamicFormInnerService} from '../lib/dynamic-form-inner.service';

@Directive()
export abstract class AbstractDynamicFormFieldDirective {

  constructor(@Inject(DYNAMIC_FORMS_SERVICE)
              readonly service: DynamicFormsService,
              readonly innerService: DynamicFormInnerService,
              readonly elementRef: ElementRef,
  ) {
    this.initCheckUnique();
  }

  private _field: FormFieldInstance;

  get field() {
    return this._field;
  }

  @Input() set field(value: FormFieldInstance) {
    this._field = value;
    this.writeValue0(value.value);
    this.runValidators();
  }

  private checkUniqueSubject = new Subject<string>();
  private lastValue: string;

  errors: string[] = [];
  touched = false;

  setValue(value: string) {
    this.touched = true;
    this.writeValue0(value);
    this.runValidators();
  }

  showAutocomplete(value: BoAutocompleteOption[]) { }

  closeAutocomplete() { }

  touch() {
    if (this.touched) {
      return;
    }
    this.runValidators();
    this.touched = true;
  }

  changeValue(value: string) {
    this.lastValue = value;
    this.touched = true;
    this.runValidators();
    this.field.value = value;
    this.innerService.changeValue(this._field.fieldId, value);
  }

  abstract validate(value: string): string[];

  abstract writeValue(value: string);

  private runValidators() {
    this.errors = [];

    this.validateRequired();
    this.validateUnique();

    const strings = this.validate(this.lastValue);
    if (strings) {
      this.errors.push(...strings);
    }

    this.innerService.reduceValidStatus();

  }

  writeValue0(value: string) {
    this.lastValue = value;
    this.writeValue(value);
  }

  private validateRequired() {
    if (!this.lastValue && this._field.isRequired) {
      this.errors.push('validate_required');
    }
  }

  private validateUnique() {
    if (this._field.isUnique) {
      this.checkUniqueSubject.next(this.lastValue);
    }
  }

  private initCheckUnique() {
    this.checkUniqueSubject.asObservable()
      .pipe(
        filter((val) => !!val),
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.innerService.autocompleteClicked)
      )
      .subscribe(async (val) => {
        const isValid = await this.service.validateForUnique(
          this.innerService.ownerData.ownerBoId, this._field.fieldId, val
        );
        if (!isValid) {
          this.errors = ['validate_unique'];
        }
      });
  }

  scrollToElement() {
    this.elementRef.nativeElement.scrollIntoView({behavior: 'smooth'});
  }
}
